import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Trash2,
  Clock,
  Smartphone,
  Monitor,
  Globe,
  Languages,
  ChevronDown,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface RoutingRule {
  id: string;
  type: 'time' | 'os' | 'device' | 'country' | 'language';
  condition: Record<string, unknown>;
  destination: string;
}

interface RoutingRulesEditorProps {
  rules: RoutingRule[];
  onChange: (rules: RoutingRule[]) => void;
}

const ruleTypeConfig = {
  time: { icon: Clock, label: 'Time of Day', color: 'text-blue-500' },
  os: { icon: Smartphone, label: 'Operating System', color: 'text-green-500' },
  device: { icon: Monitor, label: 'Device Type', color: 'text-purple-500' },
  country: { icon: Globe, label: 'Country', color: 'text-orange-500' },
  language: { icon: Languages, label: 'Language', color: 'text-pink-500' },
};

export function RoutingRulesEditor({ rules, onChange }: RoutingRulesEditorProps) {
  const [showAddRule, setShowAddRule] = useState(false);

  const addRule = () => {
    const newRule: RoutingRule = {
      id: crypto.randomUUID(),
      type: 'time',
      condition: { start_hour: 9, end_hour: 17 },
      destination: '',
    };
    onChange([...rules, newRule]);
    setShowAddRule(false);
  };

  const updateRule = (id: string, updates: Partial<RoutingRule>) => {
    onChange(rules.map((rule) => (rule.id === id ? { ...rule, ...updates } : rule)));
  };

  const removeRule = (id: string) => {
    onChange(rules.filter((rule) => rule.id !== id));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium flex items-center gap-2">
            Conditional Routing
            <Badge variant="secondary" className="text-xs">Advanced</Badge>
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Redirect users to different destinations based on time, OS, device, or location
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={addRule}>
          <Plus className="h-4 w-4 mr-2" />
          Add Rule
        </Button>
      </div>

      <AnimatePresence>
        {rules.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-8 border-2 border-dashed rounded-lg"
          >
            <Info className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No routing rules set</p>
            <p className="text-xs text-muted-foreground mt-1">
              All users will see the default destination
            </p>
          </motion.div>
        )}

        {rules.map((rule, index) => {
          const config = ruleTypeConfig[rule.type];
          const Icon = config.icon;

          return (
            <motion.div
              key={rule.id}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              className="border rounded-lg p-4 space-y-3"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-md bg-muted ${config.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Rule {index + 1}</p>
                    <p className="text-xs text-muted-foreground">{config.label}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => removeRule(rule.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Rule Type</Label>
                  <Select
                    value={rule.type}
                    onValueChange={(value: RoutingRule['type']) =>
                      updateRule(rule.id, {
                        type: value,
                        condition:
                          value === 'time'
                            ? { start_hour: 9, end_hour: 17 }
                            : value === 'os'
                            ? { os: 'ios' }
                            : value === 'device'
                            ? { device_type: 'mobile' }
                            : value === 'country'
                            ? { countries: ['US'] }
                            : { language: 'en' },
                      })
                    }
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="time">Time of Day</SelectItem>
                      <SelectItem value="os">Operating System</SelectItem>
                      <SelectItem value="device">Device Type</SelectItem>
                      <SelectItem value="country">Country</SelectItem>
                      <SelectItem value="language">Language</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Time-based condition */}
                {rule.type === 'time' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Start Hour (UTC)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="23"
                        value={(rule.condition.start_hour as number) || 0}
                        onChange={(e) =>
                          updateRule(rule.id, {
                            condition: { ...rule.condition, start_hour: parseInt(e.target.value) },
                          })
                        }
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">End Hour (UTC)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="24"
                        value={(rule.condition.end_hour as number) || 24}
                        onChange={(e) =>
                          updateRule(rule.id, {
                            condition: { ...rule.condition, end_hour: parseInt(e.target.value) },
                          })
                        }
                        className="h-9"
                      />
                    </div>
                  </div>
                )}

                {/* OS-based condition */}
                {rule.type === 'os' && (
                  <div>
                    <Label className="text-xs">Operating System</Label>
                    <Select
                      value={(rule.condition.os as string) || 'ios'}
                      onValueChange={(value) =>
                        updateRule(rule.id, { condition: { os: value } })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ios">iOS</SelectItem>
                        <SelectItem value="android">Android</SelectItem>
                        <SelectItem value="windows">Windows</SelectItem>
                        <SelectItem value="macos">macOS</SelectItem>
                        <SelectItem value="linux">Linux</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Device-based condition */}
                {rule.type === 'device' && (
                  <div>
                    <Label className="text-xs">Device Type</Label>
                    <Select
                      value={(rule.condition.device_type as string) || 'mobile'}
                      onValueChange={(value) =>
                        updateRule(rule.id, { condition: { device_type: value } })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mobile">Mobile</SelectItem>
                        <SelectItem value="tablet">Tablet</SelectItem>
                        <SelectItem value="desktop">Desktop</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Country-based condition */}
                {rule.type === 'country' && (
                  <div>
                    <Label className="text-xs">Countries (comma-separated codes)</Label>
                    <Input
                      placeholder="US,CA,GB"
                      value={(rule.condition.countries as string[])?.join(',') || ''}
                      onChange={(e) =>
                        updateRule(rule.id, {
                          condition: { countries: e.target.value.split(',').map((c) => c.trim()) },
                        })
                      }
                      className="h-9"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Use ISO 3166-1 alpha-2 codes (e.g., US, GB, FR)
                    </p>
                  </div>
                )}

                {/* Language-based condition */}
                {rule.type === 'language' && (
                  <div>
                    <Label className="text-xs">Language Code</Label>
                    <Input
                      placeholder="en, es, fr"
                      value={(rule.condition.language as string) || ''}
                      onChange={(e) =>
                        updateRule(rule.id, { condition: { language: e.target.value } })
                      }
                      className="h-9"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      ISO 639-1 code (e.g., en for English, es for Spanish)
                    </p>
                  </div>
                )}

                <div>
                  <Label className="text-xs">Destination URL</Label>
                  <Input
                    placeholder="https://example.com"
                    value={rule.destination}
                    onChange={(e) => updateRule(rule.id, { destination: e.target.value })}
                    className="h-9"
                  />
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {rules.length > 0 && (
        <div className="p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
          <p className="font-medium mb-1">How it works:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Rules are checked in order from top to bottom</li>
            <li>The first matching rule determines the destination</li>
            <li>If no rules match, the default QR destination is used</li>
          </ul>
        </div>
      )}
    </div>
  );
}
